import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, of } from 'rxjs';

import { ApiUrlBuilder } from '../../config/api.config';
import { ClientColorSettingsUpdateRequest, ClientSettings, ReportModel } from '../../models/client-settings.model';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class ClientSettingsApiService {
  private readonly http = inject(HttpClient);

  private readonly authService = inject(AuthService);

  private readonly transloco = inject(TranslocoService);

  /**
   * Busca as configurações visuais do cliente
   * associado ao usuário autenticado.
   *
   * GET /api/client-theme
   */
  async getCurrent(): Promise<ClientSettings | null> {
    return firstValueFrom(
      this.http
        .get<ClientSettings>(ApiUrlBuilder.clientSettings('current'), {
          headers: this.authService.getAuthHeaders(),
        })
        .pipe(
          catchError((error: unknown) => {
            console.error('Erro ao buscar configurações visuais do cliente atual:', error);

            return of(null);
          }),
        ),
    );
  }

  /**
   * Busca as configurações visuais de um cliente
   * pelo identificador.
   *
   * GET /api/client-theme/{clientId}
   */
  async getByClientId(clientId: string): Promise<ClientSettings | null> {
    return firstValueFrom(
      this.http
        .get<ClientSettings>(ApiUrlBuilder.clientSettings('getById', clientId), {
          headers: this.authService.getAuthHeaders(),
        })
        .pipe(
          catchError((error: unknown) => {
            console.error(`Erro ao buscar configurações visuais do cliente ${clientId}:`, error);

            return of(null);
          }),
        ),
    );
  }

  /**
   * Atualiza o tema e as cores do cliente.
   *
   * PUT /api/client-theme/{clientId}/colors
   */
  async updateColors(clientId: string, data: ClientColorSettingsUpdateRequest): Promise<ClientSettings> {
    return firstValueFrom(
      this.http.put<ClientSettings>(ApiUrlBuilder.clientSettings('updateColors', clientId), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  /**
   * Atualiza o modelo de relatório do cliente. Propaga o erro para tratamento na UI.
   *
   * PUT /api/client-theme/{clientId}/report-model
   */
  async updateReportModel(clientId: string, reportModel: ReportModel | null): Promise<void> {
    await firstValueFrom(
      this.http.put(
        ApiUrlBuilder.clientSettings('reportModel', clientId),
        { reportModel },
        {
          headers: this.authService.getAuthHeaders(),
        },
      ),
    );
  }

  /**
   * Envia ou substitui o logotipo do cliente.
   *
   * PUT /api/client-theme/{clientId}/logo
   *
   * O arquivo é enviado como multipart/form-data.
   * O navegador adiciona automaticamente o boundary.
   */
  async updateLogo(clientId: string, file: File): Promise<ClientSettings> {
    const formData = new FormData();

    formData.append('file', file, file.name);

    return firstValueFrom(
      this.http.put<ClientSettings>(ApiUrlBuilder.clientSettings('updateLogo', clientId), formData, {
        headers: this.getMultipartAuthHeaders(),
      }),
    );
  }

  /**
   * Remove o logotipo atual do cliente.
   *
   * DELETE /api/client-theme/{clientId}/logo
   */
  async removeLogo(clientId: string): Promise<ClientSettings> {
    return firstValueFrom(
      this.http.delete<ClientSettings>(ApiUrlBuilder.clientSettings('updateLogo', clientId), {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  /**
   * Restaura as configurações visuais do cliente.
   *
   * PATCH /api/client-theme/{clientId}/reset
   */
  async reset(clientId: string): Promise<ClientSettings> {
    return firstValueFrom(
      this.http.patch<ClientSettings>(
        ApiUrlBuilder.clientSettings('reset', clientId),
        {},
        {
          headers: this.authService.getAuthHeaders(),
        },
      ),
    );
  }

  /**
   * Obtém os headers de autenticação sem definir
   * manualmente o Content-Type.
   *
   * Para FormData, o navegador deve gerar:
   *
   * multipart/form-data; boundary=...
   */
  private getMultipartAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      ...this.authService.getAuthHeaders(),
    };

    delete headers['Content-Type'];
    delete headers['content-type'];

    return headers;
  }

  /**
   * Converte erros HTTP em mensagens adequadas
   * para exibição na interface.
   */
  getErrorMessage(error: HttpErrorResponse): string {
    const message = this.extractMessageFromError(error);

    if (message) {
      return message;
    }

    return this.getStatusErrorMessage(error?.status);
  }

  /**
   * Extrai a mensagem de erro do corpo da resposta,
   * quando disponível.
   */
  private extractMessageFromError(error: HttpErrorResponse): string | null {
    if (!error?.error) {
      return null;
    }

    if (typeof error.error === 'string') {
      return error.error;
    }

    if (typeof error.error.message === 'string') {
      return error.error.message;
    }

    if (typeof error.error.title === 'string') {
      return error.error.title;
    }

    if (typeof error.error.detail === 'string') {
      return error.error.detail;
    }

    if (Array.isArray(error.error.errors)) {
      return error.error.errors.join('\n');
    }

    if (error.error.errors && typeof error.error.errors === 'object') {
      const validationMessages = Object.values(error.error.errors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value): value is string => typeof value === 'string');

      if (validationMessages.length > 0) {
        return validationMessages.join('\n');
      }
    }

    return null;
  }

  /**
   * Retorna a mensagem padrão com base no status HTTP.
   */
  private getStatusErrorMessage(status: number): string {
    switch (status) {
      case 0:
        return this.transloco.translate('shared.clientSettings.apiErrors.connection');

      case 400:
        return this.transloco.translate('shared.clientSettings.apiErrors.invalidData');

      case 401:
        return this.transloco.translate('shared.clientSettings.apiErrors.sessionExpired');

      case 403:
        return this.transloco.translate('shared.clientSettings.apiErrors.permissionDenied');

      case 404:
        return this.transloco.translate('shared.clientSettings.apiErrors.notFound');

      case 413:
        return this.transloco.translate('shared.clientSettings.apiErrors.fileTooLarge');

      case 415:
        return this.transloco.translate('shared.clientSettings.apiErrors.unsupportedFile');

      case 500:
        return this.transloco.translate('shared.clientSettings.apiErrors.internalServer');

      default:
        return this.transloco.translate('shared.clientSettings.apiErrors.default');
    }
  }
}
